import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  StandardizedTable,
  TableColumn,
  formatCurrency,
  formatNumber,
  TrendIndicator,
} from "@/components/ui/standardized-table";
import { KpiNumber } from "@/components/ui/kpi-number";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { getKpiTooltip } from "@/lib/kpi-tooltips";

type CellValue = string | number | null | undefined;
type DashboardTableRow = Record<string, CellValue>;

interface TimelinePoint extends DashboardTableRow {
  date?: string;
  date_label?: string;
  total_revenue?: number;
  orders?: number;
  sessions?: number;
}

const toNumber = (value: CellValue): number => Number(value || 0);
const formatCellCurrency = (value: CellValue): string =>
  formatCurrency(toNumber(value));
const formatCellNumber = (value: CellValue): string =>
  formatNumber(toNumber(value));

const formatPercentage = (value: CellValue): string => {
  const num = Number(value || 0);
  return `${num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { useAccount } from "@/contexts/account.context";
import type { ProductsCardsResponse } from "@/backend-sdk/client/types.gen";
import {
  getProductsCardsApiV1ProductsCardsGet,
  getAcquisitionTimelineApiV1AcquisitionTimelineGet,
  getAcquisitionChannelGroupingApiV1AcquisitionChannelGroupingGet,
  getAcquisitionSourceMediumApiV1AcquisitionSourceMediumGet,
  getAcquisitionCampaignsApiV1AcquisitionCampaignsGet,
  getAcquisitionNewVsReturningApiV1AcquisitionNewVsReturningGet,
  getLandingPagesApiV1PagesLandingPageGet,
  apiV1AcquisitionGeoTablesGet,
  getConversionRateDemographicsV2ApiV1ConversionRateDemographicsV2Get,
} from "@/backend-sdk/client/sdk.gen";
import { formatDateBR } from "@/utils/dateFormat";
import { LineChart as TremorLineChart } from "@tremor/react";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import { Skeleton } from "@/components/ui/skeleton";
import { EstadoDoValor } from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BarChart3, ChevronUp, ChevronDown } from "lucide-react";
import type { TableQueryDTO } from "@/types/table-query";
import {
  DrilldownProvider,
  useDrilldownOptional,
} from "@/features/drilldown/drilldown.context";
import type { DrillDimension } from "@/features/drilldown/drilldown.context";
import { ActiveFiltersDisplay } from "@/features/drilldown/ActiveFiltersDisplay";

// === Tipos auxiliares ===
type Trend = "up" | "down" | "neutral";

const getTrend = (value?: number | null): Trend => {
  if (value === null || value === undefined) return "neutral";
  if (value > 0) return "up";
  if (value < 0) return "down";
  return "neutral";
};

const getComparisonParam = (comparisonType?: string): string => {
  if (comparisonType === "previousYear") return "previous_year";
  return "previous_period";
};

// === Mapa de Estados → Regiões ===
const STATE_TO_REGION: Record<string, string> = {
  "State of Acre": "norte",
  "State of Amapa": "norte",
  "State of Amazonas": "norte",
  "State of Para": "norte",
  "State of Rondonia": "norte",
  "State of Roraima": "norte",
  "State of Tocantins": "norte",
  "State of Alagoas": "nordeste",
  "State of Bahia": "nordeste",
  "State of Ceara": "nordeste",
  "State of Maranhao": "nordeste",
  "State of Paraiba": "nordeste",
  "State of Pernambuco": "nordeste",
  "State of Piaui": "nordeste",
  "State of Rio Grande do Norte": "nordeste",
  "State of Sergipe": "nordeste",
  "State of Distrito Federal": "centro-oeste",
  "State of Goias": "centro-oeste",
  "State of Mato Grosso": "centro-oeste",
  "State of Mato Grosso do Sul": "centro-oeste",
  "State of Espirito Santo": "sudeste",
  "State of Minas Gerais": "sudeste",
  "State of Rio de Janeiro": "sudeste",
  "State of Sao Paulo": "sudeste",
  "State of Parana": "sul",
  "State of Rio Grande do Sul": "sul",
  "State of Santa Catarina": "sul",
};

const REGION_LABELS: Record<string, string> = {
  norte: "REGIÃO NORTE",
  nordeste: "REGIÃO NORDESTE",
  "centro-oeste": "REGIÃO CENTRO OESTE",
  sudeste: "REGIÃO SUDESTE",
  sul: "REGIÃO SUL",
};

const REGION_COLORS: Record<string, string> = {
  sudeste: "bg-red-600",
  sul: "bg-blue-600",
  "centro-oeste": "bg-yellow-500",
  nordeste: "bg-orange-500",
  norte: "bg-green-600",
};

// Cores de fundo leves para os cards do mapa (rgba com baixa opacidade)
const REGION_BG_COLORS: Record<string, string> = {
  norte: "rgba(34, 197, 94, 0.12)", // verde claro
  nordeste: "rgba(249, 115, 22, 0.12)", // laranja claro
  "centro-oeste": "rgba(234, 179, 8, 0.12)", // amarelo claro
  sudeste: "rgba(239, 68, 68, 0.12)", // vermelho claro
  sul: "rgba(59, 130, 246, 0.12)", // azul claro
};

const REGION_BORDER_COLORS: Record<string, string> = {
  norte: "rgba(34, 197, 94, 0.35)",
  nordeste: "rgba(249, 115, 22, 0.35)",
  "centro-oeste": "rgba(234, 179, 8, 0.35)",
  sudeste: "rgba(239, 68, 68, 0.35)",
  sul: "rgba(59, 130, 246, 0.35)",
};

// === Render TM com heatmap verde ===
const renderTmHeatmap = (
  value: number,
  allValues: number[],
): React.ReactNode => {
  if (!value) return formatCurrency(0);
  // Sem heatmap se não há valores de referência — exibe valor simples
  if (allValues.length === 0) return formatCurrency(value);
  const min = Math.min(...allValues.filter((v) => v > 0));
  const max = Math.max(...allValues);
  const range = max - min;
  const ratio = range > 0 ? (value - min) / range : 0.5;
  const opacity = 0.15 + ratio * 0.55;
  return (
    <span
      className="inline-block px-2 py-0.5 rounded"
      style={{ backgroundColor: `rgba(34, 197, 94, ${opacity})` }}
    >
      {formatCurrency(value)}
    </span>
  );
};

// === Formatadores de KPI (padrão do sistema) ===
const kpiFormatCurrency = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    n,
  );
const kpiFormatNumber = (n: number) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(n);
const kpiFormatDecimal = (n: number) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
const kpiFormatPercent = (n: number) =>
  `${new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)}%`;

/** Os sete cards saem do relatório de itens do GA4 (`/products/cards`). */
const FONTES_DOS_CARDS: readonly RequisitoDeFonte[] = ["ga4"];

/**
 * <M-204> O valor de um card.
 *
 * Os catorze campos de `/products/cards` vêm sempre, e o valor continua **não** sendo
 * `Number(campo || 0)`, o `0` que M-203 tirou de todos os sete. O que falta é o card que
 * ainda não tem resposta: indicador enquanto a busca está em voo, e N/D com o motivo quando
 * ela falhou ou a loja não integrou o GA4 (a rota devolve zero nesse caso).
 */
const ValorDoCard: React.FC<{
  value: number | undefined;
  format: (n: number) => string;
  ariaLabel: string;
  carregando: boolean;
  erro: boolean;
}> = ({ value, format, ariaLabel, carregando, erro }) => (
  <EstadoDoValor
    carregando={carregando}
    disponivel={value !== undefined}
    fontes={FONTES_DOS_CARDS}
    erro={erro}
    className="text-lg font-light text-muted-foreground"
  >
    <KpiNumber
      value={value ?? 0}
      format={format}
      className="font-light text-foreground"
      fontPx={18}
      ariaLabel={ariaLabel}
    />
  </EstadoDoValor>
);

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
const AnaliseTicketMedioPage: React.FC = () => {
  const hierarchies = useMemo(
    (): DrillDimension[][] => [
      ["channel_group", "source_medium", "campaign"],
      ["region", "city"],
      ["new_returning"],
      ["landing_page"],
      ["gender"],
      ["age"],
    ],
    [],
  );

  return (
    <DrilldownProvider hierarchies={hierarchies}>
      <AnaliseTicketMedioContent />
    </DrilldownProvider>
  );
};

const AnaliseTicketMedioContent: React.FC = () => {
  const drilldown = useDrilldownOptional();
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();

  // --- Parâmetros compartilhados ---
  const startDate = formatDateToISO(filterState.dateRange.startDate);
  const endDate = formatDateToISO(filterState.dateRange.endDate);
  const comparison = getComparisonParam(filterState.comparison?.type);
  const customerUuid =
    selectedAccount?.customer_uuid ||
    localStorage.getItem("active_customer_uuid") ||
    undefined;
  // accountId deve ser undefined — o backend usa customer_uuid para filtrar.
  // selectedAccount.id é o UUID da conta no backend, NÃO o property_id do GA4.
  const accountId = undefined;
  const pageSize = 10;
  const landingPageSize = 6;

  // === MÉTRICAS DO GRÁFICO ===
  const [tmChartOptions, setTmChartOptions] = useState({
    ticketMedio: true,
    sessoes: false,
  });
  const handleTmChartOptionChange = (option: keyof typeof tmChartOptions) => {
    setTmChartOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  // === ESTADOS DE DADOS ===
  const [loadingCards, setLoadingCards] = useState(true);
  const [erroCards, setErroCards] = useState(false);
  // Sem loja a busca nem sai, e a flag ficaria ligada para sempre.
  const carregandoCards = loadingCards && Boolean(customerUuid);
  /**
   * <M-204> Os sete cards do topo leem `ProductsCardsResponse`, o tipo gerado do OpenAPI.
   *
   * O estado era uma `interface TicketCardsData extends DashboardTableRow` local, com
   * catorze campos **opcionais**, alimentada por `data as TicketCardsData | null`. M-203 já
   * tinha corrigido quais campos ela declarava; o que sobrava era a forma. Os dois defeitos
   * que ela permitiu — o adaptador que escrevia nomes de uma terceira forma e os quatro
   * campos que card nenhum declarava — passaram pela assinatura de índice de
   * `DashboardTableRow` sem que o compilador visse a falta, e é o que **D-049** chama de
   * código sem contrato.
   *
   * Com o tipo gerado não há mais cast, não há mais campo opcional inventado, e os catorze
   * campos são obrigatórios porque a rota os publica sempre — o que dispensa o "N/D" por
   * campo ausente que M-203 tinha posto nos sete cards.
   */
  const [cardsData, setCardsData] = useState<ProductsCardsResponse | null>(
    null,
  );

  const [loadingTimeline, setLoadingTimeline] = useState(true);
  const [timelineData, setTimelineData] = useState<TimelinePoint[]>([]);

  // Tabela: Grupo de canais
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [channelsData, setChannelsData] = useState<DashboardTableRow[]>([]);
  const [channelsPage, setChannelsPage] = useState(1);
  const [channelsTotal, setChannelsTotal] = useState(0);
  const [channelsTotalRow, setChannelsTotalRow] =
    useState<DashboardTableRow | null>(null);
  const [channelsQuery, setChannelsQuery] = useState<
    TableQueryDTO | undefined
  >();

  // Tabela: Origem / mídia
  const SOURCE_DRILL_LEVELS = useMemo(
    () => [
      {
        dimension: "source_medium" as const,
        label: "Origem / Mídia da Sessão",
        drillDimension: "source_medium" as DrillDimension,
      },
      {
        dimension: "session_source" as const,
        label: "Origem da Sessão",
        drillDimension: "session_source" as DrillDimension,
      },
      {
        dimension: "session_medium" as const,
        label: "Meio da Sessão",
        drillDimension: "session_medium" as DrillDimension,
      },
    ],
    [],
  );
  const [sourceDrillLevel, setSourceDrillLevel] = useState(0);
  const currentSourceLevel =
    SOURCE_DRILL_LEVELS[
      Math.max(0, Math.min(sourceDrillLevel, SOURCE_DRILL_LEVELS.length - 1))
    ];
  const canSourceDrillUp = sourceDrillLevel > 0;
  const canSourceDrillDown = sourceDrillLevel < SOURCE_DRILL_LEVELS.length - 1;

  const [loadingSources, setLoadingSources] = useState(true);
  const [sourcesData, setSourcesData] = useState<DashboardTableRow[]>([]);
  const [sourcesPage, setSourcesPage] = useState(1);
  const [sourcesTotal, setSourcesTotal] = useState(0);
  const [sourcesTotalRow, setSourcesTotalRow] =
    useState<DashboardTableRow | null>(null);
  const [sourcesQuery, setSourcesQuery] = useState<TableQueryDTO | undefined>();

  const handleSourceDrillUp = useCallback(() => {
    if (drilldown) drilldown.clearFilter(currentSourceLevel.drillDimension);
    setSourceDrillLevel((lvl) => Math.max(0, lvl - 1));
    setSourcesPage(1);
  }, [drilldown, currentSourceLevel.drillDimension]);
  const handleSourceDrillDown = useCallback(() => {
    if (drilldown) drilldown.clearFilter(currentSourceLevel.drillDimension);
    setSourceDrillLevel((lvl) =>
      Math.min(SOURCE_DRILL_LEVELS.length - 1, lvl + 1),
    );
    setSourcesPage(1);
  }, [
    drilldown,
    currentSourceLevel.drillDimension,
    SOURCE_DRILL_LEVELS.length,
  ]);

  // Tabela: Campanhas
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [campaignsData, setCampaignsData] = useState<DashboardTableRow[]>([]);
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [campaignsTotalRow, setCampaignsTotalRow] =
    useState<DashboardTableRow | null>(null);
  const [campaignsQuery, setCampaignsQuery] = useState<
    TableQueryDTO | undefined
  >();

  // Tabela: Novo / recorrente
  const [loadingNvr, setLoadingNvr] = useState(true);
  const [nvrData, setNvrData] = useState<DashboardTableRow[]>([]);
  const [nvrPage, setNvrPage] = useState(1);
  const [nvrTotal, setNvrTotal] = useState(0);
  const [nvrTotalRow, setNvrTotalRow] = useState<DashboardTableRow | null>(
    null,
  );
  const [nvrQuery, setNvrQuery] = useState<TableQueryDTO | undefined>();

  // Tabela: Landing pages
  const [loadingLanding, setLoadingLanding] = useState(true);
  const [landingData, setLandingData] = useState<DashboardTableRow[]>([]);
  const [landingPage, setLandingPage] = useState(1);
  const [landingTotal, setLandingTotal] = useState(0);
  const [landingTotalRow, setLandingTotalRow] =
    useState<DashboardTableRow | null>(null);
  const [landingQuery, setLandingQuery] = useState<TableQueryDTO | undefined>();

  // Tabela: Estados / Cidades + Mapa
  const [loadingGeo, setLoadingGeo] = useState(true);
  const [statesData, setStatesData] = useState<DashboardTableRow[]>([]);
  const [statesPage, setStatesPage] = useState(1);
  const [statesTotal, setStatesTotal] = useState(0);
  const [statesTotalRow, setStatesTotalRow] =
    useState<DashboardTableRow | null>(null);
  const [citiesData, setCitiesData] = useState<DashboardTableRow[]>([]);
  const [citiesPage, setCitiesPage] = useState(1);
  const [citiesTotal, setCitiesTotal] = useState(0);
  const [citiesTotalRow, setCitiesTotalRow] =
    useState<DashboardTableRow | null>(null);
  const [allStatesForMap, setAllStatesForMap] = useState<DashboardTableRow[]>(
    [],
  );

  // Tabela: Gênero / Idade
  const [loadingDemo, setLoadingDemo] = useState(true);
  const [gendersData, setGendersData] = useState<DashboardTableRow[]>([]);
  const [gendersPage, setGendersPage] = useState(1);
  const [gendersTotal, setGendersTotal] = useState(0);
  const [gendersTotalRow, setGendersTotalRow] =
    useState<DashboardTableRow | null>(null);
  const [agesData, setAgesData] = useState<DashboardTableRow[]>([]);
  const [agesPage, setAgesPage] = useState(1);
  const [agesTotal, setAgesTotal] = useState(0);
  const [agesTotalRow, setAgesTotalRow] = useState<DashboardTableRow | null>(
    null,
  );

  // === Cross-filter: handlers ===
  const handleTableRowClick = useCallback(
    (dimension: DrillDimension, valueKey: string) => {
      return (
        row: DashboardTableRow,
        _index: number,
        event?: React.MouseEvent,
      ) => {
        if (!drilldown) return;
        const val = String(row[valueKey] || "");
        if (!val || val === "N/D") return;
        const isMultiSelect = event?.ctrlKey || event?.metaKey;
        drilldown.selectRow(dimension, val, val, isMultiSelect);
      };
    },
    [drilldown],
  );

  const isTableRowSelected = useCallback(
    (dimension: DrillDimension, valueKey: string) => {
      return (row: DashboardTableRow, _index: number): boolean => {
        if (!drilldown) return false;
        return drilldown.isSelected(dimension, String(row[valueKey] || ""));
      };
    },
    [drilldown],
  );

  const crossFilterParams = useMemo(
    () => drilldown?.apiFilters || {},
    [drilldown?.apiFilters],
  );

  const getExtraParamsExcluding = useCallback(
    (excludeDimension: string) => {
      const params = { ...crossFilterParams };
      delete params[`filter_${excludeDimension}`];
      return params;
    },
    [crossFilterParams],
  );

  // === RESET PAGINAÇÃO QUANDO FILTRO MUDA ===
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setChannelsPage(1);
    setSourcesPage(1);
    setCampaignsPage(1);
    setNvrPage(1);
    setLandingPage(1);
    setStatesPage(1);
    setCitiesPage(1);
    setGendersPage(1);
    setAgesPage(1);
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    drilldown?.apiFiltersKey,
  ]);

  // === FETCH: KPI Cards ===
  //
  // <M-204> Uma rota só, com ou sem recorte. Havia aqui um desvio: sem cross-filter os cards
  // vinham de `/products/cards`, e **com** cross-filter a tela trocava para
  // `/acquisition/kpis` e adaptava os campos, porque a primeira rota não declarava
  // `filter_*`. A rota substituta não publica as quatro métricas do relatório de itens e
  // ignora três das nove dimensões em que esta página faz drill, e foi o adaptador desse
  // desvio que zerou os sete cards a cada clique (M-203). Com `/products/cards` aceitando a
  // família `filter_*`, o desvio, o adaptador, o `hasActiveFilter`, o `kpisHonramOFiltro` e
  // o "N/D" que os cobria saem todos juntos.
  useEffect(() => {
    const fetch = async () => {
      if (!customerUuid) return;
      setLoadingCards(true);
      setErroCards(false);
      try {
        const data = (
          await getProductsCardsApiV1ProductsCardsGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
              ...crossFilterParams,
            },
            throwOnError: true,
          })
        ).data;
        setCardsData(data ?? null);
      } catch (e) {
        console.error("Erro ao carregar cards:", e);
        setCardsData(null);
        setErroCards(true);
      } finally {
        setLoadingCards(false);
      }
    };
    fetch();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    drilldown?.apiFiltersKey,
    crossFilterParams,
  ]);

  // === FETCH: Timeline (gráfico Ticket Médio) ===
  useEffect(() => {
    const fetch = async () => {
      if (!customerUuid) return;
      setLoadingTimeline(true);
      try {
        const data = (
          await getAcquisitionTimelineApiV1AcquisitionTimelineGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
              ...crossFilterParams,
            },
          })
        ).data;
        setTimelineData((data?.series || []) as unknown as TimelinePoint[]);
      } catch (e) {
        console.error("Erro ao carregar timeline:", e);
        setTimelineData([]);
      } finally {
        setLoadingTimeline(false);
      }
    };
    fetch();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    drilldown?.apiFiltersKey,
    crossFilterParams,
  ]);

  // === FETCH: Channel Grouping ===
  useEffect(() => {
    const fetch = async () => {
      if (!customerUuid) return;
      setLoadingChannels(true);
      try {
        const extraParamsChannels = getExtraParamsExcluding("channel_group");
        const data = (
          await getAcquisitionChannelGroupingApiV1AcquisitionChannelGroupingGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
                property_id: accountId,
                page: channelsPage,
                page_size: pageSize,
                q: channelsQuery?.q,
                sort_by: channelsQuery?.sortBy,
                sort_dir: channelsQuery?.sortDir,
                filters: channelsQuery?.filters?.length
                  ? JSON.stringify(channelsQuery.filters)
                  : undefined,
                ...extraParamsChannels,
              },
            },
          )
        ).data;
        setChannelsData(data?.items || []);
        setChannelsTotal(data?.pagination?.total || 0);
        setChannelsTotalRow(data?.total_row || null);
      } catch (e) {
        console.error("Erro channels:", e);
      } finally {
        setLoadingChannels(false);
      }
    };
    fetch();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    channelsPage,
    channelsQuery,
    drilldown?.apiFiltersKey,
    getExtraParamsExcluding,
  ]);

  // === FETCH: Source/Medium ===
  useEffect(() => {
    const fetch = async () => {
      if (!customerUuid) return;
      setLoadingSources(true);
      try {
        const extraParams = {
          ...getExtraParamsExcluding("source_medium"),
          dimension: currentSourceLevel.dimension,
        };
        const data = (
          await getAcquisitionSourceMediumApiV1AcquisitionSourceMediumGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
              page: sourcesPage,
              page_size: pageSize,
              q: sourcesQuery?.q,
              sort_by: sourcesQuery?.sortBy,
              sort_dir: sourcesQuery?.sortDir,
              filters: sourcesQuery?.filters?.length
                ? JSON.stringify(sourcesQuery.filters)
                : undefined,
              ...extraParams,
            },
          })
        ).data;
        setSourcesData(data?.items || []);
        setSourcesTotal(data?.pagination?.total || 0);
        setSourcesTotalRow(data?.total_row || null);
      } catch (e) {
        console.error("Erro sources:", e);
      } finally {
        setLoadingSources(false);
      }
    };
    fetch();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    sourcesPage,
    sourcesQuery,
    drilldown?.apiFiltersKey,
    currentSourceLevel.dimension,
    getExtraParamsExcluding,
  ]);

  // === FETCH: Campaigns ===
  useEffect(() => {
    const fetch = async () => {
      if (!customerUuid) return;
      setLoadingCampaigns(true);
      try {
        const data = (
          await getAcquisitionCampaignsApiV1AcquisitionCampaignsGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
              page: campaignsPage,
              page_size: pageSize,
              q: campaignsQuery?.q,
              sort_by: campaignsQuery?.sortBy,
              sort_dir: campaignsQuery?.sortDir,
              filters: campaignsQuery?.filters?.length
                ? JSON.stringify(campaignsQuery.filters)
                : undefined,
              // <M-205> Mandava `crossFilterParams` inteiro, e por isso se reduzia à
              // campanha clicada — a tabela virava uma linha só.
              ...getExtraParamsExcluding("campaign"),
            },
          })
        ).data;
        setCampaignsData(data?.items || []);
        setCampaignsTotal(data?.pagination?.total || 0);
        setCampaignsTotalRow(data?.total_row || null);
      } catch (e) {
        console.error("Erro campaigns:", e);
      } finally {
        setLoadingCampaigns(false);
      }
    };
    fetch();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    campaignsPage,
    campaignsQuery,
    drilldown?.apiFiltersKey,
    getExtraParamsExcluding,
  ]);

  // === FETCH: New vs Returning ===
  useEffect(() => {
    const fetch = async () => {
      if (!customerUuid) return;
      setLoadingNvr(true);
      try {
        const data = (
          await getAcquisitionNewVsReturningApiV1AcquisitionNewVsReturningGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
              page: nvrPage,
              page_size: pageSize,
              q: nvrQuery?.q,
              sort_by: nvrQuery?.sortBy,
              sort_dir: nvrQuery?.sortDir,
              filters: nvrQuery?.filters?.length
                ? JSON.stringify(nvrQuery.filters)
                : undefined,
              ...getExtraParamsExcluding("new_returning"),
            },
          })
        ).data;
        setNvrData(data?.items || []);
        setNvrTotal(data?.pagination?.total || 0);
        setNvrTotalRow(data?.total_row || null);
      } catch (e) {
        console.error("Erro nvr:", e);
      } finally {
        setLoadingNvr(false);
      }
    };
    fetch();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    nvrPage,
    nvrQuery,
    drilldown?.apiFiltersKey,
    getExtraParamsExcluding,
  ]);

  // === FETCH: Landing Pages ===
  useEffect(() => {
    const fetch = async () => {
      if (!customerUuid) return;
      setLoadingLanding(true);
      try {
        const data = (
          await getLandingPagesApiV1PagesLandingPageGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
              page: landingPage,
              page_size: landingPageSize,
              q: landingQuery?.q,
              sort_by: landingQuery?.sortBy,
              sort_dir: landingQuery?.sortDir,
              filters: landingQuery?.filters?.length
                ? JSON.stringify(landingQuery.filters)
                : undefined,
              ...getExtraParamsExcluding("landing_page"),
            },
          })
        ).data;
        setLandingData(data?.data || []);
        setLandingTotal(data?.pagination?.total_items || 0);
        setLandingTotalRow(data?.totals || null);
      } catch (e) {
        console.error("Erro landing:", e);
      } finally {
        setLoadingLanding(false);
      }
    };
    fetch();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    landingPage,
    landingPageSize,
    landingQuery,
    drilldown?.apiFiltersKey,
    getExtraParamsExcluding,
  ]);

  // === FETCH: Geo (Estados/Cidades) ===
  //
  // <M-205> A rota é `/acquisition/geo-tables`, e é nova. Estas duas tabelas — e o mapa do
  // Brasil que se alimenta delas — liam `states` e `cities` da resposta de
  // `/conversion-rate/cards-v2`, que publica trinta e quatro campos de KPI e nenhum dos
  // dois; o `as` era o que impedia o compilador de ver isso, e as três desenhavam a lista
  // vazia desde sempre. A leitura passa a ser tipada pelo SDK gerado.
  //
  // ⚠️ O recorte vai **inteiro**, e não por `getExtraParamsExcluding`: uma requisição serve
  // os dois blocos, e quem tira a própria dimensão de cada um é o servidor — a tabela de
  // regiões ignora `filter_region`, e a de cidades ignora `filter_city`.
  useEffect(() => {
    const fetch = async () => {
      if (!customerUuid) return;
      setLoadingGeo(true);
      try {
        const data = (
          await apiV1AcquisitionGeoTablesGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
              states_page: statesPage,
              cities_page: citiesPage,
              page_size: pageSize,
              ...crossFilterParams,
            },
          })
        ).data;
        const estados = (data?.states?.items ?? []) as DashboardTableRow[];
        setStatesData(estados);
        setStatesTotal(data?.states?.pagination?.total ?? 0);
        setStatesTotalRow(
          (data?.states?.total_row ?? null) as DashboardTableRow | null,
        );
        setCitiesData((data?.cities?.items ?? []) as DashboardTableRow[]);
        setCitiesTotal(data?.cities?.pagination?.total ?? 0);
        setCitiesTotalRow(
          (data?.cities?.total_row ?? null) as DashboardTableRow | null,
        );
        if (statesPage === 1) {
          setAllStatesForMap(estados);
        }
      } catch (e) {
        console.error("Erro geo:", e);
      } finally {
        setLoadingGeo(false);
      }
    };
    fetch();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    statesPage,
    citiesPage,
    drilldown?.apiFiltersKey,
    crossFilterParams,
  ]);

  // === FETCH: Demographics (Gênero/Idade) ===
  //
  // <M-205> O recorte vai **inteiro**, como no de geo e pelo mesmo motivo: uma requisição
  // serve os dois blocos, e quem tira a própria dimensão de cada um é o servidor — a tabela
  // de gêneros ignora `filter_gender`, e a de faixas etárias ignora `filter_age`.
  useEffect(() => {
    const fetch = async () => {
      if (!customerUuid) return;
      setLoadingDemo(true);
      try {
        const data = (
          await getConversionRateDemographicsV2ApiV1ConversionRateDemographicsV2Get(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
                account_id: accountId,
                page: agesPage,
                page_size: pageSize,
                ...crossFilterParams,
              },
            },
          )
        ).data;
        setGendersData(data?.genders?.items || []);
        setGendersTotal(data?.genders?.pagination?.total || 0);
        setGendersTotalRow(
          (data?.genders?.total_row ?? null) as DashboardTableRow | null,
        );
        setAgesData(data?.ages?.items || []);
        setAgesTotal(data?.ages?.pagination?.total || 0);
        setAgesTotalRow(
          (data?.ages?.total_row ?? null) as DashboardTableRow | null,
        );
      } catch (e) {
        console.error("Erro demographics:", e);
      } finally {
        setLoadingDemo(false);
      }
    };
    fetch();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    agesPage,
    gendersPage,
    drilldown?.apiFiltersKey,
    crossFilterParams,
  ]);

  // === DADOS: Gráfico Ticket Médio ===
  const chartData = useMemo(() => {
    return timelineData.map((p: TimelinePoint) => {
      const dateValue = p.date || p.date_label;
      const label = dateValue ? formatDateBR(dateValue) : "N/D";
      const revenue = Number(p.total_revenue || 0);
      const orders = Number(p.orders || 0);
      const ticketMedio = orders > 0 ? revenue / orders : 0;
      return {
        data: label,
        "Ticket Médio": Math.round(ticketMedio * 100) / 100,
        Sessões: Number(p.sessions || 0),
      };
    });
  }, [timelineData]);

  // Categorias e cores dinâmicas baseadas nas métricas selecionadas
  const tmChartCategories = useMemo(() => {
    const cats: string[] = [];
    if (tmChartOptions.ticketMedio) cats.push("Ticket Médio");
    if (tmChartOptions.sessoes) cats.push("Sessões");
    return cats;
  }, [tmChartOptions]);

  const tmChartColors = useMemo(() => {
    const colors: string[] = [];
    if (tmChartOptions.ticketMedio) colors.push("emerald");
    if (tmChartOptions.sessoes) colors.push("red");
    return colors;
  }, [tmChartOptions]);

  // === DADOS: Regiões para o mapa ===
  const regionMapData = useMemo(() => {
    const ALL_REGIONS = ["norte", "nordeste", "centro-oeste", "sudeste", "sul"];
    const regionAgg: Record<string, { revenue: number; transactions: number }> =
      {};

    // Inicializar todas as 5 regiões para garantir que sempre apareçam
    for (const r of ALL_REGIONS) {
      regionAgg[r] = { revenue: 0, transactions: 0 };
    }

    for (const state of allStatesForMap) {
      const regionName = state.region || "";
      const slug = STATE_TO_REGION[regionName];
      if (!slug) continue;

      regionAgg[slug].revenue += Number(state.total_revenue || 0);
      const tm = Number(state.ticket_medio || 0);
      if (tm > 0) {
        regionAgg[slug].transactions += Number(state.total_revenue || 0) / tm;
      }
    }

    return ALL_REGIONS.map((slug) => {
      const data = regionAgg[slug];
      return {
        slug,
        label: REGION_LABELS[slug] || slug.toUpperCase(),
        color: REGION_COLORS[slug] || "bg-gray-600",
        tm: data.transactions > 0 ? data.revenue / data.transactions : 0,
      };
    });
  }, [allStatesForMap]);

  // === HEATMAP: coletar todos os TM para as 3 tabelas principais ===
  const channelsTmValues = useMemo(
    () =>
      (channelsData || [])
        .map((r: DashboardTableRow) => Number(r.avg_order_value || 0))
        .filter((v: number) => v > 0),
    [channelsData],
  );
  const sourceTmValues = useMemo(
    () =>
      (sourcesData || [])
        .map((r: DashboardTableRow) => Number(r.avg_ticket || 0))
        .filter((v: number) => v > 0),
    [sourcesData],
  );
  const campaignTmValues = useMemo(
    () =>
      (campaignsData || [])
        .map((r: DashboardTableRow) => Number(r.avg_ticket || 0))
        .filter((v: number) => v > 0),
    [campaignsData],
  );

  // ============================================================================
  // COLUNAS: Tabelas grandes (Sessões, TX Conversão, Pedidos, Receita, TM)
  // ============================================================================
  const makeChannelColumns = (): TableColumn[] => [
    {
      key: "channel_group",
      header: "Grupo de canais padrão da sessão",
      align: "left" as const,
      render: (v: unknown, _item: unknown, idx: number, isTotal?: boolean) =>
        isTotal
          ? String(v)
          : `${(channelsPage - 1) * pageSize + idx + 1}. ${v}`,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right" as const,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "sessions_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "conversion_rate",
      header: "TX Conversão",
      align: "right" as const,
      render: (v: CellValue) => formatPercentage(v),
    },
    {
      key: "conversion_rate_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right" as const,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "orders_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "total_revenue",
      header: "Receita total",
      align: "right" as const,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "total_revenue_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "avg_order_value",
      header: "TM",
      align: "right" as const,
      render: (v: CellValue) =>
        renderTmHeatmap(Number(v || 0), channelsTmValues),
    },
    {
      key: "avg_order_value_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "total_users",
      header: "Total de Usuários",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "total_users_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "arpu",
      header: "ARPU",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "arpu_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "session_value",
      header: "Valor Sessão",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "session_value_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
  ];

  const makeSourceColumns = (
    page: number,
    tmValues: number[],
  ): TableColumn[] => [
    {
      key: "source_medium",
      header: currentSourceLevel.label,
      align: "left" as const,
      render: (v: unknown, _item: unknown, idx: number, isTotal?: boolean) =>
        isTotal ? String(v) : `${(page - 1) * pageSize + idx + 1}. ${v}`,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right" as const,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "conversion_rate",
      header: "TX Conversão",
      align: "right" as const,
      render: (v: CellValue) => formatPercentage(v),
    },
    {
      key: "conversion_rate_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right" as const,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "revenue",
      header: "Receita total",
      align: "right" as const,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "revenue_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "avg_ticket",
      header: "TM",
      align: "right" as const,
      render: (v: CellValue) => renderTmHeatmap(Number(v || 0), tmValues),
    },
    {
      key: "average_order_value_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "total_users",
      header: "Total de Usuários",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "total_users_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "arpu",
      header: "ARPU",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "arpu_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "session_value",
      header: "Valor Sessão",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "session_value_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
  ];

  const makeCampaignColumns = (
    page: number,
    tmValues: number[],
  ): TableColumn[] => [
    {
      key: "campaign",
      header: "Campanha da sessão",
      align: "left" as const,
      render: (v: unknown, _item: unknown, idx: number, isTotal?: boolean) =>
        isTotal ? String(v) : `${(page - 1) * pageSize + idx + 1}. ${v}`,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right" as const,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "conversion_rate",
      header: "TX Conversão",
      align: "right" as const,
      render: (v: CellValue) => formatPercentage(v),
    },
    {
      key: "conversion_rate_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right" as const,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "revenue",
      header: "Receita total",
      align: "right" as const,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "revenue_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "avg_ticket",
      header: "TM",
      align: "right" as const,
      render: (v: CellValue) => renderTmHeatmap(Number(v || 0), tmValues),
    },
    {
      key: "average_order_value_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "total_users",
      header: "Total de Usuários",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "total_users_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "arpu",
      header: "ARPU",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "arpu_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "session_value",
      header: "Valor Sessão",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "session_value_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
  ];

  // ============================================================================
  // COLUNAS: Tabelas simples (Label, Receita total, % Δ, TM, % Δ)
  // ============================================================================
  const makeSimpleColumns = (
    labelKey: string,
    labelHeader: string,
    page: number,
  ): TableColumn[] => [
    {
      key: labelKey,
      header: labelHeader,
      align: "left" as const,
      render: (v: unknown, _item: unknown, idx: number, isTotal?: boolean) =>
        isTotal ? String(v) : `${(page - 1) * pageSize + idx + 1}. ${v}`,
    },
    {
      key: "revenue",
      header: "Receita total",
      align: "right" as const,
      render: (v: CellValue) => formatCurrency(Number(v || 0)),
    },
    {
      key: "revenue_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "avg_ticket",
      header: "TM",
      align: "right" as const,
      render: (v: CellValue) => renderTmHeatmap(Number(v || 0), []),
    },
    {
      key: "average_order_value_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "conversion_rate",
      header: "TX Conversão",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatPercentage(v),
    },
    {
      key: "conversion_rate_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "total_users",
      header: "Total de Usuários",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "total_users_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "session_value",
      header: "Valor Sessão",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "session_value_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "arpu",
      header: "ARPU",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "arpu_delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
  ];

  const makeLandingColumns = (page: number): TableColumn[] => [
    {
      key: "paginaDestino",
      header: "Página de destino",
      align: "left" as const,
      render: (v: unknown, _item: unknown, idx: number, isTotal?: boolean) =>
        isTotal ? String(v) : `${(page - 1) * landingPageSize + idx + 1}. ${v}`,
    },
    {
      key: "receitaTotal",
      header: "Receita total",
      align: "right" as const,
      render: (v: CellValue) => formatCurrency(Number(v || 0)),
    },
    {
      key: "receitaTotal_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "ticketMedio",
      header: "TM",
      align: "right" as const,
      render: (v: CellValue) => renderTmHeatmap(Number(v || 0), []),
    },
    {
      key: "ticketMedio_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "pedidos",
      header: "Pedidos",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "pedidos_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatPercentage(v),
    },
    {
      key: "txConversao_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "totalUsers",
      header: "Total de Usuários",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "totalUsers_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "valorSessao",
      header: "Valor Sessão",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "valorSessao_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "arpu",
      header: "ARPU",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "arpu_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "sessoes",
      header: "Sessões",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "sessoes_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
  ];

  const makeGeoColumns = (
    labelKey: string,
    labelHeader: string,
    page: number,
  ): TableColumn[] => [
    {
      key: labelKey,
      header: labelHeader,
      align: "left" as const,
      render: (v: unknown, _item: unknown, idx: number, isTotal?: boolean) =>
        isTotal ? String(v) : `${(page - 1) * pageSize + idx + 1}. ${v}`,
    },
    {
      key: "total_revenue",
      header: "Receita total",
      align: "right" as const,
      render: (v: CellValue) => formatCurrency(Number(v || 0)),
    },
    {
      key: "total_revenue_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right" as const,
      render: (v: CellValue) => renderTmHeatmap(Number(v || 0), []),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "conversion_rate",
      header: "TX Conversão",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatPercentage(v),
    },
    {
      key: "conversion_rate_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "total_users",
      header: "Total de Usuários",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "total_users_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "session_value",
      header: "Valor Sessão",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "session_value_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "arpu",
      header: "ARPU",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellCurrency(v),
    },
    {
      key: "arpu_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right" as const,
      visible: false,
      render: (v: CellValue) => formatCellNumber(v),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (v: CellValue) => <TrendIndicator value={toNumber(v)} />,
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="p-6 space-y-6 page-background">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Análise de Ticket Médio
        </h1>
        <p className="text-muted-foreground">
          Análise detalhada do ticket médio do seu e-commerce
        </p>
      </div>

      <GlobalFilter />

      {/* Filtros Ativos de Cross-Filtering */}
      <ActiveFiltersDisplay />

      {/* ============ KPI CARDS (padrão do sistema) ============ */}
      <div className="grid gap-6 grid-cols-2 lg:grid-cols-4">
        {/* Receita do Item */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Receita do Item</span>
                <HelpTooltip
                  content={getKpiTooltip("Receita do Item")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="min-w-0">
              <ValorDoCard
                carregando={carregandoCards}
                erro={erroCards}
                value={cardsData?.item_revenue}
                format={kpiFormatCurrency}
                ariaLabel="Receita do item"
              />
            </div>
            <div className="flex items-center text-xs text-muted-foreground mt-2">
              {!carregandoCards &&
              typeof cardsData?.item_revenue_change_percentage === "number" ? (
                <TrendIndicator
                  value={Number(cardsData.item_revenue_change_percentage)}
                />
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Ticket Médio */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Ticket Médio</span>
                <HelpTooltip
                  content={getKpiTooltip("Ticket Médio")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="min-w-0">
              <ValorDoCard
                carregando={carregandoCards}
                erro={erroCards}
                value={cardsData?.average_ticket}
                format={kpiFormatCurrency}
                ariaLabel="Ticket médio"
              />
            </div>
            <div className="flex items-center text-xs text-muted-foreground mt-2">
              {!carregandoCards &&
              typeof cardsData?.average_ticket_change_percentage ===
                "number" ? (
                <TrendIndicator
                  value={Number(cardsData.average_ticket_change_percentage)}
                />
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Pedidos */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Pedidos</span>
                <HelpTooltip
                  content={getKpiTooltip("Pedidos")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="min-w-0">
              <ValorDoCard
                carregando={carregandoCards}
                erro={erroCards}
                value={cardsData?.transactions}
                format={kpiFormatNumber}
                ariaLabel="Pedidos"
              />
            </div>
            <div className="flex items-center text-xs text-muted-foreground mt-2">
              {!carregandoCards &&
              typeof cardsData?.transactions_change_percentage === "number" ? (
                <TrendIndicator
                  value={Number(cardsData.transactions_change_percentage)}
                />
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Itens Comprados */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Itens Comprados</span>
                <HelpTooltip
                  content={getKpiTooltip("Itens Comprados")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="min-w-0">
              <ValorDoCard
                carregando={carregandoCards}
                erro={erroCards}
                value={cardsData?.items_purchased}
                format={kpiFormatNumber}
                ariaLabel="Itens comprados"
              />
            </div>
            <div className="flex items-center text-xs text-muted-foreground mt-2">
              {!carregandoCards &&
              typeof cardsData?.items_purchased_change_percentage ===
                "number" ? (
                <TrendIndicator
                  value={Number(cardsData.items_purchased_change_percentage)}
                />
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 grid-cols-2 lg:grid-cols-4">
        {/* Quantidade Média de Itens */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Qtd. Média Itens</span>
                <HelpTooltip
                  content={getKpiTooltip("Qtd. Média Itens")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="min-w-0">
              <ValorDoCard
                carregando={carregandoCards}
                erro={erroCards}
                value={cardsData?.average_item_quantity}
                format={kpiFormatDecimal}
                ariaLabel="Quantidade média de itens"
              />
            </div>
            <div className="flex items-center text-xs text-muted-foreground mt-2">
              {!carregandoCards &&
              typeof cardsData?.average_item_quantity_change_percentage ===
                "number" ? (
                <TrendIndicator
                  value={Number(
                    cardsData.average_item_quantity_change_percentage,
                  )}
                />
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Valor Médio Unitário */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Valor Médio Unitário</span>
                <HelpTooltip
                  content={getKpiTooltip("Valor Médio Unitário")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="min-w-0">
              <ValorDoCard
                carregando={carregandoCards}
                erro={erroCards}
                value={cardsData?.average_unit_amount}
                format={kpiFormatCurrency}
                ariaLabel="Valor médio unitário"
              />
            </div>
            <div className="flex items-center text-xs text-muted-foreground mt-2">
              {!carregandoCards &&
              typeof cardsData?.average_unit_amount_change_percentage ===
                "number" ? (
                <TrendIndicator
                  value={Number(
                    cardsData.average_unit_amount_change_percentage,
                  )}
                />
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Taxa Conversão */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Taxa Conversão</span>
                <HelpTooltip
                  content={getKpiTooltip("Taxa Conversão")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="min-w-0">
              <ValorDoCard
                carregando={carregandoCards}
                erro={erroCards}
                value={cardsData?.session_conversion_rate}
                format={kpiFormatPercent}
                ariaLabel="Taxa de conversão"
              />
            </div>
            <div className="flex items-center text-xs text-muted-foreground mt-2">
              {!carregandoCards &&
              typeof cardsData?.session_conversion_rate_change_percentage ===
                "number" ? (
                <TrendIndicator
                  value={Number(
                    cardsData.session_conversion_rate_change_percentage,
                  )}
                />
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ============ GRÁFICO TICKET MÉDIO ============ */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-center flex-1">TICKET MÉDIO</CardTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-muted-foreground"
                >
                  <BarChart3 className="h-4 w-4 mr-1" />
                  Métricas
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuCheckboxItem
                  checked={tmChartOptions.ticketMedio}
                  onCheckedChange={() =>
                    handleTmChartOptionChange("ticketMedio")
                  }
                >
                  Ticket Médio
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={tmChartOptions.sessoes}
                  onCheckedChange={() => handleTmChartOptionChange("sessoes")}
                >
                  Sessões
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent>
          {loadingTimeline ? (
            <Skeleton className="h-72 w-full" />
          ) : chartData.length > 0 && tmChartCategories.length > 0 ? (
            <ScrollableChart>
              <TremorLineChart
                className="h-72"
                data={chartData}
                index="data"
                categories={tmChartCategories}
                colors={tmChartColors}
                valueFormatter={(v) => {
                  if (v >= 1000) return formatCellNumber(v);
                  return formatCellCurrency(v);
                }}
                showLegend={true}
                showGridLines={true}
                showAnimation={true}
                curveType="monotone"
              />
            </ScrollableChart>
          ) : (
            <div className="flex items-center justify-center h-72 text-muted-foreground">
              {tmChartCategories.length === 0
                ? "Selecione ao menos uma métrica"
                : "Sem dados disponíveis"}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ TABELA: GRUPO DE CANAIS ============ */}
      <StandardizedTable
        title="GRUPO DE CANAIS PADRÃO DA SESSÃO"
        columns={makeChannelColumns()}
        data={channelsData || []}
        total={channelsTotalRow ?? undefined}
        loading={loadingChannels}
        pagination={{
          current: channelsPage,
          pageSize,
          total: channelsTotal,
          onPageChange: setChannelsPage,
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setChannelsPage(1);
          setChannelsQuery(q);
        }}
        onRowClick={
          drilldown
            ? handleTableRowClick("channel_group", "channel_group")
            : undefined
        }
        selectedRowIndex={
          drilldown
            ? isTableRowSelected("channel_group", "channel_group")
            : undefined
        }
        clickableRows={!!drilldown}
      />

      {/* ============ TABELA: ORIGEM / MÍDIA ============ */}
      <StandardizedTable
        title={
          <div className="flex items-center justify-between gap-3 w-full">
            <div className="flex items-center gap-2">
              <span className="font-semibold">ORIGEM / MÍDIA DA SESSÃO</span>
              <span className="text-xs text-muted-foreground">
                ({currentSourceLevel.label})
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSourceDrillUp}
                disabled={!canSourceDrillUp}
                className="h-7 px-2"
                title={canSourceDrillUp ? "Drill up" : "Nível mais alto"}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSourceDrillDown}
                disabled={!canSourceDrillDown}
                className="h-7 px-2"
                title={canSourceDrillDown ? "Drill down" : "Nível mais baixo"}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          </div>
        }
        columns={makeSourceColumns(sourcesPage, sourceTmValues)}
        data={sourcesData}
        total={sourcesTotalRow ?? undefined}
        loading={loadingSources}
        pagination={{
          current: sourcesPage,
          pageSize,
          total: sourcesTotal,
          onPageChange: setSourcesPage,
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setSourcesPage(1);
          setSourcesQuery(q);
        }}
        onRowClick={
          drilldown
            ? handleTableRowClick("source_medium", "source_medium")
            : undefined
        }
        selectedRowIndex={
          drilldown
            ? isTableRowSelected("source_medium", "source_medium")
            : undefined
        }
        clickableRows={!!drilldown}
      />

      {/* ============ TABELA: CAMPANHAS ============ */}
      <StandardizedTable
        title="CAMPANHA DA SESSÃO"
        columns={makeCampaignColumns(campaignsPage, campaignTmValues)}
        data={campaignsData}
        total={campaignsTotalRow ?? undefined}
        loading={loadingCampaigns}
        pagination={{
          current: campaignsPage,
          pageSize,
          total: campaignsTotal,
          onPageChange: setCampaignsPage,
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setCampaignsPage(1);
          setCampaignsQuery(q);
        }}
        onRowClick={
          drilldown ? handleTableRowClick("campaign", "campaign") : undefined
        }
        selectedRowIndex={
          drilldown ? isTableRowSelected("campaign", "campaign") : undefined
        }
        clickableRows={!!drilldown}
      />

      {/* ============ TABELAS: NOVO/RECORRENTE + LANDING PAGES ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
        <StandardizedTable
          title="NOVO / RECORRENTE"
          columns={makeSimpleColumns(
            "new_vs_returning",
            "Novo / recorrente",
            nvrPage,
          )}
          data={nvrData}
          total={nvrTotalRow ?? undefined}
          loading={loadingNvr}
          className="!h-full"
          fillRemainingSpaceClassName="bg-muted/20"
          pagination={{
            current: nvrPage,
            pageSize,
            total: nvrTotal,
            onPageChange: setNvrPage,
          }}
          queryMode="server"
          onQueryChange={(q) => {
            setNvrPage(1);
            setNvrQuery(q);
          }}
          onRowClick={
            drilldown
              ? handleTableRowClick("new_returning", "new_vs_returning")
              : undefined
          }
          selectedRowIndex={
            drilldown
              ? isTableRowSelected("new_returning", "new_vs_returning")
              : undefined
          }
          clickableRows={!!drilldown}
        />
        <StandardizedTable
          title="PÁGINA DE DESTINO"
          columns={makeLandingColumns(landingPage)}
          data={landingData}
          total={landingTotalRow ?? undefined}
          loading={loadingLanding}
          pagination={{
            current: landingPage,
            pageSize: landingPageSize,
            total: landingTotal,
            onPageChange: setLandingPage,
          }}
          queryMode="server"
          onQueryChange={(q) => {
            setLandingPage(1);
            setLandingQuery(q);
          }}
          onRowClick={
            drilldown
              ? handleTableRowClick("landing_page", "paginaDestino")
              : undefined
          }
          selectedRowIndex={
            drilldown
              ? isTableRowSelected("landing_page", "paginaDestino")
              : undefined
          }
          clickableRows={!!drilldown}
        />
      </div>

      {/* ============ TABELAS: REGIÃO + CIDADE ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StandardizedTable
          title="REGIÃO"
          columns={makeGeoColumns("region", "Região", statesPage)}
          data={statesData}
          total={statesTotalRow ?? undefined}
          loading={loadingGeo}
          pagination={{
            current: statesPage,
            pageSize,
            total: statesTotal,
            onPageChange: setStatesPage,
          }}
          onRowClick={
            drilldown ? handleTableRowClick("region", "region") : undefined
          }
          selectedRowIndex={
            drilldown ? isTableRowSelected("region", "region") : undefined
          }
          clickableRows={!!drilldown}
        />
        <StandardizedTable
          title="CIDADE"
          columns={makeGeoColumns("city", "Cidade", citiesPage)}
          data={citiesData}
          total={citiesTotalRow ?? undefined}
          loading={loadingGeo}
          pagination={{
            current: citiesPage,
            pageSize,
            total: citiesTotal,
            onPageChange: setCitiesPage,
          }}
          onRowClick={
            drilldown ? handleTableRowClick("city", "city") : undefined
          }
          selectedRowIndex={
            drilldown ? isTableRowSelected("city", "city") : undefined
          }
          clickableRows={!!drilldown}
        />
      </div>

      {/* ============ MAPA + GÊNERO + IDADE ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Mapa do Brasil com TM por região */}
        <Card className="lg:col-span-4">
          <CardContent className="p-4">
            <div
              className="relative overflow-hidden"
              style={{ minHeight: "460px" }}
            >
              <img
                src="/mapaBrasil.png"
                alt="Mapa do Brasil por Regiões"
                className="absolute"
                style={{
                  width: "420%",
                  maxWidth: "none",
                  height: "auto",
                  left: "48%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
              {/* Cards TM por região sobrepostos ao mapa */}
              {regionMapData.map((region) => {
                const positionStyles: Record<string, React.CSSProperties> = {
                  norte: { top: "8%", left: "4px" },
                  nordeste: { top: "20%", right: "4px" },
                  "centro-oeste": { top: "52%", left: "4px" },
                  sudeste: { top: "46%", right: "4px" },
                  sul: {
                    bottom: "6%",
                    left: "50%",
                    transform: "translateX(-50%)",
                  },
                };
                return (
                  <div
                    key={region.slug}
                    className="absolute z-10"
                    style={positionStyles[region.slug] || {}}
                  >
                    <div
                      className="backdrop-blur-sm rounded-lg px-2 py-1 shadow-sm"
                      style={{
                        backgroundColor:
                          REGION_BG_COLORS[region.slug] ||
                          "rgba(255,255,255,0.9)",
                        border: `1px solid ${REGION_BORDER_COLORS[region.slug] || "rgba(0,0,0,0.1)"}`,
                      }}
                    >
                      <p className="text-2xs text-muted-foreground font-medium whitespace-nowrap">
                        TM - {region.label}
                      </p>
                      <p className="text-sm font-semibold text-foreground">
                        {formatCurrency(region.tm)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Tabela Gênero */}
        <div className="lg:col-span-4">
          <StandardizedTable
            title="GÊNERO"
            columns={makeGeoColumns("gender", "Gênero", gendersPage)}
            data={gendersData}
            loading={loadingDemo}
            total={gendersTotalRow ?? undefined}
            pagination={{
              current: gendersPage,
              pageSize,
              total: gendersTotal,
              onPageChange: setGendersPage,
            }}
            onRowClick={
              drilldown ? handleTableRowClick("gender", "gender") : undefined
            }
            selectedRowIndex={
              drilldown ? isTableRowSelected("gender", "gender") : undefined
            }
            clickableRows={!!drilldown}
          />
        </div>

        {/* Tabela Idade */}
        <div className="lg:col-span-4">
          <StandardizedTable
            title="IDADE"
            columns={makeGeoColumns("age", "Idade", agesPage)}
            data={agesData}
            loading={loadingDemo}
            total={agesTotalRow ?? undefined}
            pagination={{
              current: agesPage,
              pageSize,
              total: agesTotal,
              onPageChange: setAgesPage,
            }}
            onRowClick={
              drilldown ? handleTableRowClick("age", "age") : undefined
            }
            selectedRowIndex={
              drilldown ? isTableRowSelected("age", "age") : undefined
            }
            clickableRows={!!drilldown}
          />
        </div>
      </div>
    </div>
  );
};

export default AnaliseTicketMedioPage;
